import type { FC } from 'react';
import { memo, useCallback, useEffect, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { Alert, Box, List, ListItem, Skeleton, Typography } from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import { ReadOnlyTextField, Select } from 'components/common';
import type { Option } from 'components/common';
import datadogActions from 'lib/datadog';
import { useLocationParams } from 'hooks';
import { useTwitterAdSettings } from '../api';
import type { TwitterAdSettingsPayload, TwitterFlowSettings } from '../api';
import useTwitterLineItemsData from '../hooks/useTwitterLineItemsData';
import { findTextById, getDefaultLocale, getDefaultTextId } from '../utils/adSettingsDefaults';
import { getTwitterApiErrorMessage } from '../hooks/useTwitterCampaignsData';

export const NO_TEXTS_MESSAGE = 'No texts available for this flow';
export const AD_SETTINGS_ERROR_MESSAGE = 'Failed to load ad settings';

const AdSettingsSkeleton: FC = () => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 2, width: '100%', maxWidth: 800 }}>
    {[0, 1, 2].map((key) => (
      <Skeleton key={key} height={40} sx={{ transform: 'translateY(0)', borderRadius: '8px' }} />
    ))}
  </Box>
);

interface FlowAdSettingsProps {
  flow: string;
  settings: TwitterFlowSettings;
  lineItemNames: string[];
}

const FlowAdSettings: FC<FlowAdSettingsProps> = memo(({ flow, settings, lineItemNames }) => {
  const { setValue } = useFormContext();
  const selectedTextId = useWatch({ name: `adSettings.${flow}.text_id` }) as string | undefined;
  const selectedText = useMemo(
    () => findTextById(settings.texts, selectedTextId),
    [settings.texts, selectedTextId],
  );

  const textOptions = useMemo<Option[]>(
    () => settings.texts.map((text) => ({ value: String(text.id), label: text.name })),
    [settings.texts],
  );
  const localeOptions = useMemo<Option[]>(
    () => (selectedText?.locales ?? []).map((locale) => ({ value: locale, label: locale.toUpperCase() })),
    [selectedText],
  );

  const handleTextChange = useCallback(
    (event: { target: { value: unknown } }) => {
      const nextText = findTextById(settings.texts, String(event.target.value ?? ''));
      setValue(`adSettings.${flow}.locale`, getDefaultLocale(nextText), { shouldValidate: true });
    },
    [flow, settings.texts, setValue],
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        gap: 2,
        p: 2,
        borderBottom: 1,
        borderColor: 'divider',
      }}
      data-testid={`twitter-ad-settings-${flow}`}
    >
      <Box sx={{ flex: 1, minWidth: 200 }}>
        <Typography variant="subtitle1" fontWeight="medium">
          {flow}
        </Typography>
        {lineItemNames.map((name) => (
          <Typography key={name} variant="caption" color="text.secondary" display="block">
            {name}
          </Typography>
        ))}
        <Box
          sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mt: 1 }}
          data-testid={`twitter-ad-settings-url-${flow}`}
        >
          <LinkIcon fontSize="small" color="action" sx={{ mt: '1px' }} />
          <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
            {settings.url}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ flex: 4 }}>
        {settings.texts.length === 0 ? (
          <Alert severity="warning">{NO_TEXTS_MESSAGE}</Alert>
        ) : (
          <List sx={{ display: 'flex', flexDirection: 'column', gap: 3, p: 0 }} disablePadding>
            <ListItem sx={{ display: 'flex', gap: 2, px: 0, alignItems: 'flex-start' }}>
              <Box sx={{ flex: 3, minWidth: 0 }}>
                <Select
                  name={`adSettings.${flow}.text_id`}
                  inputLabel="Text"
                  options={textOptions}
                  handleChange={handleTextChange}
                  sx={{ '.MuiInputBase-select': { whiteSpace: 'wrap' } }}
                />
              </Box>
              <Box sx={{ flex: 1, minWidth: 140 }}>
                <Select
                  name={`adSettings.${flow}.locale`}
                  inputLabel="Locale"
                  options={localeOptions}
                  disabled={!localeOptions.length}
                />
              </Box>
            </ListItem>
            {selectedText?.title !== undefined && (
              <ListItem sx={{ px: 0 }}>
                <ReadOnlyTextField label="Title" value={selectedText.title} />
              </ListItem>
            )}
          </List>
        )}
      </Box>
    </Box>
  );
});

FlowAdSettings.displayName = 'FlowAdSettings';

const AdSettingsStep: FC = memo(() => {
  const { setValue, getValues, trigger } = useFormContext();
  const app = (useWatch({ name: 'app' }) as string | undefined) ?? '';
  const adSettings = useWatch({ name: 'adSettings' }) as
    | Record<string, TwitterAdSettingsPayload | undefined>
    | undefined;
  const { setAdSettingsFlow: setAdSettingsFlowParam } = useLocationParams();
  const { selectedFlows, selectedLineItemRows } = useTwitterLineItemsData();

  useEffect(() => {
    setAdSettingsFlowParam(adSettings ?? {});
  }, [adSettings, setAdSettingsFlowParam]);

  const { data, isLoading, isError, error, isSuccess } = useTwitterAdSettings({
    params: { app, flows: selectedFlows },
  });

  const lineItemNamesByFlow = useMemo(() => {
    const byFlow = new Map<string, string[]>();
    selectedLineItemRows.forEach((row) => {
      if (!row.flow) return;
      byFlow.set(row.flow, [...(byFlow.get(row.flow) ?? []), row.line_item_name]);
    });
    return byFlow;
  }, [selectedLineItemRows]);

  useEffect(() => {
    if (!isSuccess || !data) return;

    const current = (getValues('adSettings') ?? {}) as Record<string, TwitterAdSettingsPayload | undefined>;
    const next: Record<string, TwitterAdSettingsPayload | undefined> = {};

    Object.entries(data).forEach(([flow, settings]) => {
      const existing = current[flow];
      const existingText = findTextById(settings.texts, existing?.text_id);
      if (existing && existingText && existingText.locales.includes(existing.locale)) {
        next[flow] = existing;
        return;
      }

      const textId = getDefaultTextId(settings.texts);
      next[flow] = {
        text_id: textId,
        locale: getDefaultLocale(findTextById(settings.texts, textId)),
      };
    });

    setValue('adSettings', next);
    setValue('$adSettingsData', data);
    void trigger();
  }, [data, getValues, isSuccess, setValue, trigger]);

  useEffect(() => {
    if (!isError || !error) return;

    datadogActions.trackDatadogError(error as Error, {
      source: 'twitter_ad_settings_query',
      app,
      flowsCount: selectedFlows.length,
    });
  }, [isError, error, app, selectedFlows.length]);

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h6" gutterBottom>
        Ad Settings
      </Typography>
      {isLoading ? (
        <AdSettingsSkeleton />
      ) : isError ? (
        <Alert severity="error" data-testid="twitter-ad-settings-error">
          {AD_SETTINGS_ERROR_MESSAGE}: {getTwitterApiErrorMessage(error, 'unknown error')}
        </Alert>
      ) : !data || Object.keys(data).length === 0 ? (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            No ad settings found
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', width: '100%', flexDirection: 'column' }}>
          {Object.entries(data).map(([flow, settings]) => (
            <FlowAdSettings
              key={flow}
              flow={flow}
              settings={settings}
              lineItemNames={lineItemNamesByFlow.get(flow) ?? []}
            />
          ))}
        </Box>
      )}
    </Box>
  );
});

AdSettingsStep.displayName = 'AdSettingsStep';

export default AdSettingsStep;
